import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./RsvpModal.css";

// ─── Helpers ───────────────────────────────────────────────────

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "textarea:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Lazily create (or reuse) the portal root so index.html stays untouched.
function getPortalRoot() {
  if (typeof document === "undefined") return null;
  let root = document.getElementById("rsvp-modal-root");
  if (!root) {
    root = document.createElement("div");
    root.id = "rsvp-modal-root";
    document.body.appendChild(root);
  }
  return root;
}

// ─── Component ─────────────────────────────────────────────────

/**
 * RsvpModal
 * Portal-mounted dialog that hosts the full RSVP flow. It owns no
 * flow state itself — each flow screen is rendered as a child and
 * reads/writes its own state. This component is responsible for the
 * modal shell only: backdrop, container, close button, scroll-lock,
 * focus trap, ESC + backdrop-click dismiss, and route-change guard.
 *
 * Props:
 *   open                  — controlled open state
 *   onClose               — called when the user requests a close
 *   submitting            — disables close affordances while a POST is in flight
 *   hasUnsavedProgress    — when true, a close attempt prompts for confirmation
 *   labelledBy            — id of the heading inside the child that labels the dialog
 *   children              — the active flow screen
 */
const RsvpModal = ({
  open,
  onClose,
  submitting = false,
  hasUnsavedProgress = false,
  labelledBy = "rsvp-modal-title",
  children,
}) => {
  const { t } = useTranslation();
  // We unmount immediately when `open` flips false. The enter animation
  // runs via the `.is-open` class toggled one frame after mount (see the
  // effect below). Skipping an exit animation keeps the lifecycle
  // trivial — no exit-pending state, no transition bookkeeping.
  const shouldRender = open;

  const [entered, setEntered] = useState(false); // drives the `.is-open` class
  const backdropRef = useRef(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null); // element focused before open — we restore on close
  const savedBodyOverflowRef = useRef(null);

  const location = useLocation();
  const initialLocationRef = useRef(location);

  // Capture the previously-focused element the moment we open. Bump the
  // enter class on the next frame so the CSS transition runs.
  useEffect(() => {
    if (!open) return undefined;
    triggerRef.current =
      typeof document !== "undefined" ? document.activeElement : null;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => {
      cancelAnimationFrame(raf);
      setEntered(false);
    };
  }, [open]);

  // Close requests flow through here so we can centralize the unsaved-progress
  // confirm and the submitting guard.
  const requestClose = useCallback(
    (reason = "user") => {
      if (submitting) return;
      if (reason !== "route" && hasUnsavedProgress) {
        // window.confirm is a deliberate choice — see plan Non-goals: a
        // styled replacement is a follow-up.
        const ok = window.confirm(t("rsvp.modal.confirmDiscard"));
        if (!ok) return;
      }
      onClose?.();
    },
    [submitting, hasUnsavedProgress, onClose, t],
  );

  // ESC to close. Bound to window so it works regardless of focus target.
  useEffect(() => {
    if (!shouldRender) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        requestClose("esc");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [shouldRender, requestClose]);

  // Scroll-lock. Restore the prior overflow value so we compose safely
  // with anything else that might lock scroll in the future.
  useLayoutEffect(() => {
    if (!shouldRender) return undefined;
    const body = document.body;
    const html = document.documentElement;
    savedBodyOverflowRef.current = body.style.overflow;
    body.style.overflow = "hidden";
    body.classList.add("rsvp-modal-open");
    html.classList.add("rsvp-modal-open");
    return () => {
      body.style.overflow = savedBodyOverflowRef.current ?? "";
      body.classList.remove("rsvp-modal-open");
      html.classList.remove("rsvp-modal-open");
    };
  }, [shouldRender]);

  // Route change guard — close silently if the user navigates away while
  // the modal is open.
  useEffect(() => {
    if (!shouldRender) {
      initialLocationRef.current = location;
      return;
    }
    if (location !== initialLocationRef.current) {
      requestClose("route");
    }
  }, [location, shouldRender, requestClose]);

  // Initial focus. Runs once per mount (when shouldRender flips true).
  useEffect(() => {
    if (!shouldRender) return;
    const raf = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;
      const focusables = container.querySelectorAll(FOCUSABLE_SELECTOR);
      // Prefer the first non-close button so the X isn't the initial focus
      // on opening. Fall back to the close button if nothing else is focusable.
      const firstReal = Array.from(focusables).find(
        (el) => !el.classList.contains("rsvp-modal-close"),
      );
      (firstReal || focusables[0])?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [shouldRender]);

  // Focus trap — keep Tab/Shift+Tab cycling within the modal container.
  useEffect(() => {
    if (!shouldRender) return undefined;
    const handler = (e) => {
      if (e.key !== "Tab") return;
      const container = containerRef.current;
      if (!container) return;
      const focusables = Array.from(
        container.querySelectorAll(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled"));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [shouldRender]);

  // Restore focus to the triggering element when the modal closes.
  useEffect(() => {
    if (shouldRender) return undefined;
    return () => {
      // Only restore focus if the trigger is still in the DOM.
      const el = triggerRef.current;
      if (el && typeof el.focus === "function" && document.contains(el)) {
        el.focus();
      }
    };
  }, [shouldRender]);

  const handleBackdropMouseDown = (e) => {
    // Only close if the mousedown started on the backdrop itself — this
    // avoids accidental closes when dragging a text selection out of the
    // container.
    if (e.target === backdropRef.current) {
      requestClose("backdrop");
    }
  };

  if (!shouldRender) return null;

  const portalRoot = getPortalRoot();
  if (!portalRoot) return null;

  return createPortal(
    <div
      ref={backdropRef}
      className={`rsvp-modal-backdrop${entered ? " is-open" : ""}`}
      onMouseDown={handleBackdropMouseDown}
      aria-hidden="false"
    >
      <div
        ref={containerRef}
        className="rsvp-modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="rsvp-modal-close"
          onClick={() => requestClose("button")}
          disabled={submitting}
          aria-label={t("rsvp.modal.closeAriaLabel")}
        >
          {/* Minimalist X — matches the editorial tone. */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1 1L13 13M13 1L1 13"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className="rsvp-modal-body">{children}</div>
      </div>
    </div>,
    portalRoot,
  );
};

export default RsvpModal;
